import { type BannerRender, getItem } from "../../../shared/shop-catalog";
import { drawCharacterSprite } from "./CharacterSprite";
import { drawCustomGrid } from "./CustomArt";

const LIZARDS_BG_CHARS = ["char.emerald", "char.coral", "char.indigo"];

export function drawBannerBg(
  ctx: CanvasRenderingContext2D,
  cfg: BannerRender,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
): void {
  ctx.save();
  // clip so animated content doesn't escape the rounded rect
  ctx.beginPath();
  fillRoundedRectPath(ctx, x, y, w, h, 6);
  ctx.clip();

  switch (cfg.kind) {
    case "solid":
      ctx.fillStyle = cfg.a;
      ctx.fillRect(x, y, w, h);
      break;
    case "gradient": {
      const grad = ctx.createLinearGradient(x, y, x + w, y);
      grad.addColorStop(0, cfg.a);
      grad.addColorStop(1, cfg.b ?? cfg.a);
      ctx.fillStyle = grad;
      ctx.fillRect(x, y, w, h);
      break;
    }
    case "stripes": {
      ctx.fillStyle = cfg.a;
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = cfg.b ?? cfg.a;
      const stripeW = 8;
      for (let sx = x; sx < x + w; sx += stripeW * 2) ctx.fillRect(sx, y, stripeW, h);
      break;
    }
    case "lizards":
      drawLizardsBanner(ctx, cfg, x, y, w, h, time);
      break;
    case "god":
      drawGodBanner(ctx, cfg, x, y, w, h, time);
      break;
    case "milestone":
      drawMilestoneBanner(ctx, cfg, x, y, w, h, time);
      break;
    case "custom":
      if (cfg.cells && cfg.grid) {
        // Tiled to fill the wide banner without distorting the square grid. When
        // animated, the tiles scroll horizontally.
        const tile = h;
        ctx.fillStyle = cfg.a;
        ctx.fillRect(x, y, w, h);
        const off = cfg.animated ? (time * 24) % tile : 0;
        for (let tx = x - off; tx < x + w; tx += tile) {
          drawCustomGrid(ctx, cfg.cells, cfg.grid, tx, y, tile, h);
        }
      }
      break;
  }

  ctx.restore();
}

// A two-color field tiled with a shimmering, scrolling label (e.g. "50", "100").
function drawMilestoneBanner(
  ctx: CanvasRenderingContext2D,
  cfg: BannerRender,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
): void {
  const grad = ctx.createLinearGradient(x, y, x + w, y);
  grad.addColorStop(0, cfg.a);
  grad.addColorStop(0.5, cfg.b ?? cfg.a);
  grad.addColorStop(1, cfg.a);
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);

  const label = cfg.label ?? "★";
  const rows = h >= 30 ? 2 : 1;
  const rowH = h / rows;
  const fs = Math.max(7, Math.round(rowH * 0.7));
  ctx.font = `900 ${fs}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = Math.max(1, fs * 0.14);
  const stepX = ctx.measureText(label).width + fs * 0.7;
  const scroll = (time * 28) % stepX;

  for (let r = 0; r < rows; r += 1) {
    const cy = y + rowH * (r + 0.5);
    const brick = (r % 2) * (stepX / 2); // offset alternate rows
    for (let cx = x - stepX + ((scroll + brick) % stepX); cx < x + w + stepX; cx += stepX) {
      const shimmer = 0.55 + 0.45 * Math.sin(time * 4 + cx * 0.05 + r * 1.7);
      ctx.strokeStyle = "rgba(20,30,20,0.5)";
      ctx.strokeText(label, cx, cy);
      ctx.fillStyle = `rgba(255,255,255,${(0.45 + shimmer * 0.55).toFixed(3)})`;
      ctx.fillText(label, cx, cy);
    }
  }
}

function drawLizardsBanner(
  ctx: CanvasRenderingContext2D,
  cfg: BannerRender,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
): void {
  // base gradient
  const grad = ctx.createLinearGradient(x, y, x + w, y);
  grad.addColorStop(0, cfg.a);
  grad.addColorStop(1, cfg.b ?? cfg.a);
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);

  // subtle starfield dots
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  for (let i = 0; i < 6; i += 1) {
    const sx = x + ((i * 53 + time * 25) % w);
    const sy = y + ((i * 17) % h);
    ctx.fillRect(sx, sy, 1, 1);
  }

  // flying lizards
  const cy = y + h / 2;
  const scale = h / 36;
  for (let i = 0; i < 3; i += 1) {
    const speed = 35 + i * 12;
    const lane = (i - 1) * (h * 0.28);
    const pos = ((time * speed + i * 110) % (w + 60)) - 30;
    const lx = x + pos;
    const ly = cy + lane + Math.sin(time * 3 + i * 1.7) * (h * 0.15);
    const item = getItem(LIZARDS_BG_CHARS[i % LIZARDS_BG_CHARS.length]!);
    if (!item || item.slot !== "character") continue;
    ctx.save();
    ctx.translate(lx, ly);
    ctx.rotate(Math.sin(time * 4 + i) * 0.25);
    ctx.scale(scale, scale);
    drawCharacterSprite(ctx, 14, item.render, {
      bobTime: time + i,
      flapTime: (time + i * 0.3) % 0.4 < 0.15 ? 0.1 : 0,
    });
    ctx.restore();
  }
}

function drawGodBanner(
  ctx: CanvasRenderingContext2D,
  cfg: BannerRender,
  x: number,
  y: number,
  w: number,
  h: number,
  time: number,
): void {
  // black background
  ctx.fillStyle = cfg.a;
  ctx.fillRect(x, y, w, h);

  // pulsing radial light
  const cx = x + w / 2;
  const cy = y + h / 2;
  const pulse = 0.55 + Math.sin(time * 2.5) * 0.25;
  const rad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(w, h) * 0.55);
  const accent = cfg.b ?? "#ffd700";
  rad.addColorStop(0, withAlpha(accent, pulse));
  rad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = rad;
  ctx.fillRect(x, y, w, h);

  // shimmering "GOD" text
  ctx.save();
  ctx.shadowColor = accent;
  ctx.shadowBlur = 8 * pulse;
  const hue = (time * 30) % 360;
  ctx.fillStyle = `hsl(${hue + 45}, 90%, 70%)`;
  ctx.font = `bold ${Math.round(h * 0.95)}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("GOD", cx, cy + 1);
  ctx.restore();

  // sparkle dots
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  for (let i = 0; i < 5; i += 1) {
    const sx = x + ((time * 80 + i * 73) % w);
    const sy = y + h * (0.2 + (i % 3) * 0.3);
    ctx.fillRect(sx, sy, 1.5, 1.5);
  }
}

function withAlpha(hex: string, alpha: number): string {
  // accepts "#rgb" / "#rrggbb"
  const a = Math.max(0, Math.min(1, alpha));
  if (hex.length === 4) {
    const r = parseInt(hex[1]! + hex[1]!, 16);
    const g = parseInt(hex[2]! + hex[2]!, 16);
    const b = parseInt(hex[3]! + hex[3]!, 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function fillRoundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
